import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

interface Patient {
  id: string;
  nom: string;
  prenom: string;
  date_naissance: string;
  telephone: string;
  email: string;
  adresse: string;
  groupe_sanguin: string;
  allergies: string;
  antecedents_medicaux: string;
  maladies_chroniques: string;
  status: string;
  is_provisional: boolean;
  created_at: string;
}

interface RendezVous {
  id: string;
  date_rdv: string;
  heure_debut: string;
  motif: string;
  statut: string;
  diagnostique?: string;
  traitement?: string;
  medicaments?: string;
  notes_medecin?: string;
  type_consultation?: string;
  medecins: {
    nom: string;
    prenom: string;
    specialite: string;
  };
}

interface Prescription {
  id: string;
  medicaments: string;
  posologie?: string;
  duree_traitement?: string;
  instructions_particulieres?: string;
  date_creation: string;
  medecins: {
    nom: string;
    prenom: string;
    specialite: string;
  };
}

interface Suivi {
  id: string;
  objet: string;
  message: string;
  diagnostique?: string;
  traitement?: string;
  notes_medicales?: string;
  statut: string;
  date_creation: string;
  medecin_expediteur: {
    nom: string;
    prenom: string;
    specialite: string;
  };
  medecin_destinataire: {
    nom: string;
    prenom: string;
    specialite: string;
  };
}

interface ResultatTest {
  id: string;
  type_test: string;
  titre: string;
  description?: string;
  commentaire_medecin?: string;
  date_test: string;
}

export default function AdminPatientsPage() {
  const navigate = useNavigate();
  const { user, userRoles } = useAuth();

  const [searchTerm, setSearchTerm] = useState('');
  const [patients, setPatients] = useState<Patient[]>([]);
  const [allPatients, setAllPatients] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [patientRdv, setPatientRdv] = useState<RendezVous[]>([]);
  const [patientPrescriptions, setPatientPrescriptions] = useState<Prescription[]>([]);
  const [patientSuivis, setPatientSuivis] = useState<Suivi[]>([]);
  const [patientTests, setPatientTests] = useState<ResultatTest[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [activeTab, setActiveTab] = useState<'consultations' | 'prescriptions' | 'suivis' | 'tests'>('consultations');
  const [patientToDelete, setPatientToDelete] = useState<Patient | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const isAdmin = userRoles?.includes('super_admin') || userRoles?.includes('admin');
  const isTrueSuperAdmin = userRoles?.includes('super_admin');

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    if (!isAdmin) {
      navigate('/', { replace: true });
      return;
    }
    loadAllPatients();
  }, [user, isAdmin]);

  const loadAllPatients = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .order('nom')
        .order('prenom');

      if (error) throw error;
      setAllPatients(data || []);
      setPatients(data || []);
    } catch (err) {
      console.error('Erreur chargement patients:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!searchTerm.trim()) {
      setPatients(allPatients);
      return;
    }
    const term = searchTerm.toLowerCase();
    setPatients(
      allPatients.filter(
        (p) =>
          p.nom?.toLowerCase().includes(term) ||
          p.prenom?.toLowerCase().includes(term) ||
          p.telephone?.toLowerCase().includes(term) ||
          p.email?.toLowerCase().includes(term)
      )
    );
  }, [searchTerm, allPatients]);

  const loadPatientHistory = async (patient: Patient) => {
    setSelectedPatient(patient);
    setLoadingHistory(true);
    setActiveTab('consultations');

    try {
      const [rdvRes, prescRes, suiviRes, testsRes] = await Promise.all([
        supabase
          .from('rendez_vous')
          .select(`*, medecins(nom, prenom, specialite)`)
          .eq('patient_id', patient.id)
          .order('date_rdv', { ascending: false })
          .order('heure_debut', { ascending: false }),
        supabase
          .from('prescriptions')
          .select(`*, medecins(nom, prenom, specialite)`)
          .eq('patient_id', patient.id)
          .order('date_creation', { ascending: false }),
        supabase
          .from('suivis_patients')
          .select(`*, medecin_expediteur:medecins!medecin_expediteur_id(nom, prenom, specialite), medecin_destinataire:medecins!medecin_destinataire_id(nom, prenom, specialite)`)
          .eq('patient_id', patient.id)
          .order('date_creation', { ascending: false }),
        supabase
          .from('resultats_tests')
          .select('*')
          .eq('patient_id', patient.id)
          .order('date_test', { ascending: false }),
      ]);

      setPatientRdv(rdvRes.data || []);
      setPatientPrescriptions(prescRes.data || []);
      setPatientSuivis(suiviRes.data || []);
      setPatientTests(testsRes.data || []);
    } catch (err) {
      console.error('Erreur chargement historique:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleDeletePatient = async () => {
    if (!patientToDelete) {
      console.warn('[DELETE] handleDeletePatient appelé sans patientToDelete');
      return;
    }
    console.log('[DELETE] Début suppression du patient:', patientToDelete.id, patientToDelete.nom, patientToDelete.prenom);
    setDeleting(true);
    setDeleteError('');
    try {
      console.log('[DELETE] Appel edge function delete-patient...');
      const { data, error } = await supabase.functions.invoke('delete-patient', {
        body: { patientId: patientToDelete.id },
      });
      console.log('[DELETE] Réponse reçue — data:', JSON.stringify(data), 'error:', JSON.stringify(error));
      if (error) {
        console.error('[DELETE] Erreur invoke:', JSON.stringify(error));
        throw new Error(error.message || 'Erreur lors de la suppression');
      }
      if (!data?.success) {
        console.error('[DELETE] Succès=false, détails:', JSON.stringify(data));
        throw new Error(data?.error || 'Erreur lors de la suppression');
      }

      console.log('[DELETE] Suppression réussie, rafraîchissement de la liste...');
      // Rafraichir la liste
      setSelectedPatient(null);
      setPatientToDelete(null);
      setPatientRdv([]);
      setPatientPrescriptions([]);
      setPatientSuivis([]);
      setPatientTests([]);
      await loadAllPatients();
      console.log('[DELETE] Liste rafraîchie avec succès');
    } catch (err: any) {
      console.error('[DELETE] ERREUR catch:', err.message || err, '| stack:', err.stack);
      setDeleteError(err.message || 'Une erreur est survenue lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const getStatutBadge = (statut: string) => {
    const styles: Record<string, string> = {
      en_attente: 'bg-amber-100 text-amber-800',
      confirme: 'bg-sky-100 text-sky-800',
      termine: 'bg-emerald-100 text-emerald-800',
      annule: 'bg-red-100 text-red-800',
      en_cours: 'bg-violet-100 text-violet-800',
    };
    return styles[statut] || 'bg-gray-100 text-gray-700';
  };

  const getStatutLabel = (statut: string) => {
    const labels: Record<string, string> = {
      en_attente: 'En attente',
      confirme: 'Confirmé',
      termine: 'Terminé',
      annule: 'Annulé',
      en_cours: 'En cours',
    };
    return labels[statut] || statut;
  };

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Gestion des Patients</h1>
            <p className="text-sm text-gray-500 mt-1">
              {allPatients.length} patient{allPatients.length > 1 ? 's' : ''} enregistré{allPatients.length > 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            Retour
          </button>
        </div>

        {/* Barre de recherche */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg w-5 h-5 flex items-center justify-center"></i>
            <input
              type="text"
              placeholder="Rechercher un patient par nom, prénom, téléphone ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-circle-line text-lg w-5 h-5 flex items-center justify-center"></i>
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line text-4xl text-teal-500 animate-spin w-8 h-8 flex items-center justify-center"></i>
            <span className="ml-3 text-gray-500">Chargement des patients...</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Liste des patients */}
            <div className="lg:col-span-1 bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-100 bg-gray-50/50">
                <h2 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
                  <i className="ri-group-line w-4 h-4 flex items-center justify-center text-teal-600"></i>
                  Patients ({patients.length})
                </h2>
              </div>
              <div className="divide-y divide-gray-100 max-h-[calc(100vh-320px)] overflow-y-auto">
                {patients.length === 0 ? (
                  <div className="p-8 text-center text-gray-400">
                    <i className="ri-user-search-line text-4xl mb-3 block w-10 h-10 mx-auto flex items-center justify-center"></i>
                    <p className="text-sm">Aucun patient trouvé</p>
                  </div>
                ) : (
                  patients.map((patient) => (
                    <div
                      key={patient.id}
                      onClick={() => loadPatientHistory(patient)}
                      className={`group p-4 cursor-pointer transition-colors hover:bg-teal-50 ${
                        selectedPatient?.id === patient.id
                          ? 'bg-teal-50 border-l-[3px] border-teal-600'
                          : 'border-l-[3px] border-transparent'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-gray-900 text-sm truncate">
                              {patient.nom} {patient.prenom}
                            </h3>
                            {patient.is_provisional && (
                              <span className="shrink-0 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-medium whitespace-nowrap">
                                Provisoire
                              </span>
                            )}
                          </div>
                          <div className="mt-1.5 flex flex-col gap-0.5 text-xs text-gray-500">
                            {patient.telephone && (
                              <span className="flex items-center gap-1.5">
                                <i className="ri-phone-line w-3 h-3 flex items-center justify-center"></i>
                                {patient.telephone}
                              </span>
                            )}
                            {patient.email && (
                              <span className="flex items-center gap-1.5 truncate">
                                <i className="ri-mail-line w-3 h-3 flex items-center justify-center"></i>
                                {patient.email}
                              </span>
                            )}
                            <span className="flex items-center gap-1.5">
                              <i className="ri-calendar-line w-3 h-3 flex items-center justify-center"></i>
                              Né(e) le {formatDate(patient.date_naissance)}
                            </span>
                          </div>
                        </div>
                        <i className="ri-arrow-right-s-line text-gray-300 w-4 h-4 flex items-center justify-center ml-2 shrink-0"></i>
                      </div>
                      {isTrueSuperAdmin && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('[DELETE] Clic sur bouton suppression patient:', patient.id, patient.nom, patient.prenom);
                            setPatientToDelete(patient);
                          }}
                          className="mt-2 flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
                          title="Supprimer ce patient"
                        >
                          <i className="ri-delete-bin-line w-3.5 h-3.5 flex items-center justify-center"></i>
                          Supprimer
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Historique du patient */}
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
              {!selectedPatient ? (
                <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                  <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                    <i className="ri-user-search-line text-3xl text-gray-300 w-8 h-8 flex items-center justify-center"></i>
                  </div>
                  <p className="text-sm font-medium text-gray-500">Sélectionnez un patient</p>
                  <p className="text-xs text-gray-400 mt-1">pour voir son historique médical complet</p>
                </div>
              ) : loadingHistory ? (
                <div className="flex items-center justify-center py-20">
                  <i className="ri-loader-4-line text-3xl text-teal-500 animate-spin w-8 h-8 flex items-center justify-center"></i>
                  <span className="ml-3 text-gray-500 text-sm">Chargement de l'historique...</span>
                </div>
              ) : (
                <>
                  {/* En-tête patient */}
                  <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-teal-50/50 to-white">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">
                          {selectedPatient.prenom} {selectedPatient.nom}
                        </h2>
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500">
                          <span className="flex items-center gap-1.5">
                            <i className="ri-calendar-line w-4 h-4 flex items-center justify-center text-gray-400"></i>
                            Né(e) le {formatDate(selectedPatient.date_naissance)}
                          </span>
                          {selectedPatient.groupe_sanguin && (
                            <span className="px-2 py-0.5 bg-red-50 text-red-700 rounded-full text-xs font-semibold">
                              {selectedPatient.groupe_sanguin}
                            </span>
                          )}
                          {selectedPatient.is_provisional && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                              Compte provisoire
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => navigate(`/medecin/dossier-patient/${selectedPatient.id}`)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 text-white text-xs rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-folder-open-line w-3.5 h-3.5 flex items-center justify-center"></i>
                          Dossier complet
                        </button>
                        {isTrueSuperAdmin && (
                          <button
                            onClick={() => {
                              console.log('[DELETE] Clic suppression depuis en-tête patient:', selectedPatient.id);
                              setPatientToDelete(selectedPatient);
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 border border-red-200 text-xs rounded-lg hover:bg-red-100 transition-colors whitespace-nowrap cursor-pointer"
                            title="Supprimer ce patient"
                          >
                            <i className="ri-delete-bin-line w-3.5 h-3.5 flex items-center justify-center"></i>
                            Supprimer
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Infos médicales */}
                    {(selectedPatient.allergies || selectedPatient.antecedents_medicaux || selectedPatient.maladies_chroniques || selectedPatient.telephone || selectedPatient.email) && (
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                        {selectedPatient.telephone && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <i className="ri-phone-line w-4 h-4 flex items-center justify-center text-gray-400"></i>
                            <span>{selectedPatient.telephone}</span>
                          </div>
                        )}
                        {selectedPatient.email && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <i className="ri-mail-line w-4 h-4 flex items-center justify-center text-gray-400"></i>
                            <span className="truncate">{selectedPatient.email}</span>
                          </div>
                        )}
                        {selectedPatient.allergies && (
                          <div className="flex items-start gap-2 text-sm">
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium shrink-0 mt-0.5">Allergies</span>
                            <span className="text-gray-600">{selectedPatient.allergies}</span>
                          </div>
                        )}
                        {selectedPatient.maladies_chroniques && (
                          <div className="flex items-start gap-2 text-sm">
                            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-medium shrink-0 mt-0.5">Chroniques</span>
                            <span className="text-gray-600">{selectedPatient.maladies_chroniques}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Onglets */}
                  <div className="border-b border-gray-100 px-5">
                    <div className="flex gap-1">
                      {[
                        { key: 'consultations' as const, icon: 'ri-stethoscope-line', label: 'Consultations', count: patientRdv.length },
                        { key: 'prescriptions' as const, icon: 'ri-file-text-line', label: 'Prescriptions', count: patientPrescriptions.length },
                        { key: 'suivis' as const, icon: 'ri-send-plane-line', label: 'Suivis', count: patientSuivis.length },
                        { key: 'tests' as const, icon: 'ri-flask-line', label: 'Résultats', count: patientTests.length },
                      ].map((tab) => (
                        <button
                          key={tab.key}
                          onClick={() => setActiveTab(tab.key)}
                          className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                            activeTab === tab.key
                              ? 'border-teal-600 text-teal-700'
                              : 'border-transparent text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          <i className={`${tab.icon} w-4 h-4 flex items-center justify-center`}></i>
                          {tab.label}
                          {tab.count > 0 && (
                            <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[11px] font-semibold ${
                              activeTab === tab.key
                                ? 'bg-teal-100 text-teal-700'
                                : 'bg-gray-100 text-gray-500'
                            }`}>
                              {tab.count}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Contenu des onglets */}
                  <div className="max-h-[calc(100vh-420px)] overflow-y-auto p-5">
                    {/* Consultations */}
                    {activeTab === 'consultations' && (
                      <div>
                        {patientRdv.length === 0 ? (
                          <div className="text-center py-12 text-gray-400">
                            <i className="ri-calendar-event-line text-4xl mb-3 block w-10 h-10 mx-auto flex items-center justify-center"></i>
                            <p className="text-sm">Aucune consultation enregistrée</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {patientRdv.map((rdv) => (
                              <div key={rdv.id} className="border border-gray-100 rounded-xl p-4 hover:border-gray-200 transition-colors">
                                <div className="flex items-start justify-between mb-3">
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-semibold text-gray-900">
                                        {formatDate(rdv.date_rdv)} à {rdv.heure_debut?.substring(0, 5)}
                                      </span>
                                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${getStatutBadge(rdv.statut)}`}>
                                        {getStatutLabel(rdv.statut)}
                                      </span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                      Dr. {rdv.medecins?.nom} {rdv.medecins?.prenom}
                                      {rdv.medecins?.specialite && <span> · {rdv.medecins.specialite}</span>}
                                    </p>
                                  </div>
                                  {rdv.type_consultation && (
                                    <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-[11px] font-medium whitespace-nowrap">
                                      {rdv.type_consultation}
                                    </span>
                                  )}
                                </div>

                                {rdv.motif && (
                                  <div className="mb-2 text-sm">
                                    <span className="text-gray-500 font-medium">Motif :</span>{' '}
                                    <span className="text-gray-700">{rdv.motif}</span>
                                  </div>
                                )}
                                {rdv.notes_medecin && (
                                  <div className="mb-2 text-sm">
                                    <span className="text-gray-500 font-medium">Notes :</span>{' '}
                                    <span className="text-gray-700">{rdv.notes_medecin}</span>
                                  </div>
                                )}
                                {rdv.diagnostique && (
                                  <div className="mb-2 text-sm">
                                    <span className="text-emerald-600 font-medium">Diagnostic :</span>{' '}
                                    <span className="text-gray-700">{rdv.diagnostique}</span>
                                  </div>
                                )}
                                {rdv.traitement && (
                                  <div className="mb-2 text-sm">
                                    <span className="text-sky-600 font-medium">Traitement :</span>{' '}
                                    <span className="text-gray-700">{rdv.traitement}</span>
                                  </div>
                                )}
                                {rdv.medicaments && (
                                  <div className="text-sm">
                                    <span className="text-violet-600 font-medium">Médicaments :</span>{' '}
                                    <span className="text-gray-700">{rdv.medicaments}</span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Prescriptions */}
                    {activeTab === 'prescriptions' && (
                      <div>
                        {patientPrescriptions.length === 0 ? (
                          <div className="text-center py-12 text-gray-400">
                            <i className="ri-file-text-line text-4xl mb-3 block w-10 h-10 mx-auto flex items-center justify-center"></i>
                            <p className="text-sm">Aucune prescription enregistrée</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {patientPrescriptions.map((presc) => (
                              <div key={presc.id} className="border border-gray-100 rounded-xl p-4 hover:border-gray-200 transition-colors">
                                <div className="flex items-start justify-between mb-3">
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <i className="ri-file-text-line w-4 h-4 flex items-center justify-center text-green-600"></i>
                                      <span className="text-sm font-semibold text-gray-900">Prescription</span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                      Dr. {presc.medecins?.nom} {presc.medecins?.prenom} · {formatDate(presc.date_creation)}
                                    </p>
                                  </div>
                                </div>
                                <div className="space-y-2 text-sm">
                                  <p><span className="text-gray-500 font-medium">Médicaments :</span> <span className="text-gray-700">{presc.medicaments}</span></p>
                                  {presc.posologie && <p><span className="text-gray-500 font-medium">Posologie :</span> <span className="text-gray-700">{presc.posologie}</span></p>}
                                  {presc.duree_traitement && <p><span className="text-gray-500 font-medium">Durée :</span> <span className="text-gray-700">{presc.duree_traitement}</span></p>}
                                  {presc.instructions_particulieres && <p><span className="text-gray-500 font-medium">Instructions :</span> <span className="text-gray-700">{presc.instructions_particulieres}</span></p>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Suivis */}
                    {activeTab === 'suivis' && (
                      <div>
                        {patientSuivis.length === 0 ? (
                          <div className="text-center py-12 text-gray-400">
                            <i className="ri-send-plane-line text-4xl mb-3 block w-10 h-10 mx-auto flex items-center justify-center"></i>
                            <p className="text-sm">Aucun suivi médical enregistré</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {patientSuivis.map((suivi) => (
                              <div key={suivi.id} className="border border-gray-100 rounded-xl p-4 hover:border-gray-200 transition-colors">
                                <div className="flex items-start justify-between mb-3">
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <i className="ri-send-plane-line w-4 h-4 flex items-center justify-center text-indigo-600"></i>
                                      <span className="text-sm font-semibold text-gray-900">{suivi.objet}</span>
                                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                                        suivi.statut === 'actif' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'
                                      }`}>
                                        {suivi.statut}
                                      </span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                      De : Dr. {suivi.medecin_expediteur?.nom} {suivi.medecin_expediteur?.prenom}
                                      {' → '}
                                      Dr. {suivi.medecin_destinataire?.nom} {suivi.medecin_destinataire?.prenom}
                                      {' · '}{formatDate(suivi.date_creation)}
                                    </p>
                                  </div>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
                                  <p><span className="text-gray-500 font-medium">Message :</span> <span className="text-gray-700">{suivi.message}</span></p>
                                  {suivi.diagnostique && <p><span className="text-gray-500 font-medium">Diagnostic :</span> <span className="text-gray-700">{suivi.diagnostique}</span></p>}
                                  {suivi.traitement && <p><span className="text-gray-500 font-medium">Traitement :</span> <span className="text-gray-700">{suivi.traitement}</span></p>}
                                  {suivi.notes_medicales && <p><span className="text-gray-500 font-medium">Notes :</span> <span className="text-gray-700">{suivi.notes_medicales}</span></p>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Tests */}
                    {activeTab === 'tests' && (
                      <div>
                        {patientTests.length === 0 ? (
                          <div className="text-center py-12 text-gray-400">
                            <i className="ri-flask-line text-4xl mb-3 block w-10 h-10 mx-auto flex items-center justify-center"></i>
                            <p className="text-sm">Aucun résultat de test enregistré</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {patientTests.map((test) => (
                              <div key={test.id} className="border border-gray-100 rounded-xl p-4 hover:border-gray-200 transition-colors">
                                <div className="flex items-start justify-between mb-3">
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <i className="ri-flask-line w-4 h-4 flex items-center justify-center text-orange-600"></i>
                                      <span className="text-sm font-semibold text-gray-900">{test.titre}</span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                      {test.type_test} · {formatDate(test.date_test)}
                                    </p>
                                  </div>
                                </div>
                                {test.description && (
                                  <p className="text-sm text-gray-700 mb-2">{test.description}</p>
                                )}
                                {test.commentaire_medecin && (
                                  <div className="bg-blue-50 rounded-lg p-3 text-sm">
                                    <span className="text-blue-700 font-medium">Commentaire médecin :</span>{' '}
                                    <span className="text-gray-700">{test.commentaire_medecin}</span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <Footer />

      {/* Modal de confirmation de suppression */}
      {patientToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-alert-line text-2xl text-red-600 w-6 h-6 flex items-center justify-center"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Supprimer ce patient ?</h3>
              <p className="text-sm text-gray-600">
                Vous allez supprimer définitivement{' '}
                <strong>{patientToDelete.prenom} {patientToDelete.nom}</strong>
                {patientToDelete.email && <> ({patientToDelete.email})</>}
                .
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-2">
                <i className="ri-error-warning-line text-amber-600 w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                <div className="text-xs text-amber-800">
                  <p className="font-semibold mb-1">Cette action est irréversible</p>
                  <p>Toutes les données associées seront supprimées : consultations, prescriptions, suivis, résultats d'analyses, dossiers médicaux, et le compte utilisateur.</p>
                </div>
              </div>
            </div>

            {deleteError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-xl text-sm flex items-start">
                <i className="ri-error-warning-line mr-2 mt-0.5 shrink-0 w-4 h-4 flex items-center justify-center"></i>
                <span>{deleteError}</span>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => { setPatientToDelete(null); setDeleteError(''); }}
                disabled={deleting}
                className="flex-1 px-6 py-2.5 border-2 border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-all font-semibold whitespace-nowrap cursor-pointer text-sm disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleDeletePatient}
                disabled={deleting}
                className="flex-1 px-6 py-2.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition-all font-semibold whitespace-nowrap cursor-pointer text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                    Suppression...
                  </>
                ) : (
                  <>
                    <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                    Supprimer définitivement
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}